// ORB descriptors are binary, so descriptor distance is measured by
// counting differing bits with Hamming distance.
const POPCOUNT_TABLE = new Uint8Array(256);

for (let value = 0; value < 256; value += 1) {
  let bits = value;
  let count = 0;

  while (bits !== 0) {
    bits &= bits - 1;
    count += 1;
  }

  POPCOUNT_TABLE[value] = count;
}

export function hammingDistance(
  dataA,
  offsetA,
  dataB,
  offsetB,
  descriptorLength,
) {
  let distance = 0;

  for (let i = 0; i < descriptorLength; i += 1) {
    const difference = dataA[offsetA + i] ^ dataB[offsetB + i];
    distance += POPCOUNT_TABLE[difference];
  }

  return distance;
}

// Find the nearest and second-nearest descriptor for Lowe's ratio test.
function findBestMatch(sourceDescriptors, sourceIndex, targetDescriptors) {
  const descriptorLength = sourceDescriptors.cols;
  const sourceOffset = sourceIndex * descriptorLength;

  let bestIndex = -1;
  let bestDistance = Infinity;
  let secondBestDistance = Infinity;

  for (
    let targetIndex = 0;
    targetIndex < targetDescriptors.rows;
    targetIndex += 1
  ) {
    const targetOffset = targetIndex * descriptorLength;

    const distance = hammingDistance(
      sourceDescriptors.data,
      sourceOffset,
      targetDescriptors.data,
      targetOffset,
      descriptorLength,
    );

    if (distance < bestDistance) {
      secondBestDistance = bestDistance;
      bestDistance = distance;
      bestIndex = targetIndex;
    } else if (distance < secondBestDistance) {
      secondBestDistance = distance;
    }
  }

  if (bestIndex === -1 || !Number.isFinite(secondBestDistance)) {
    return null;
  }

  const ratio =
    secondBestDistance === 0
      ? bestDistance === 0
        ? 0
        : Infinity
      : bestDistance / secondBestDistance;

  return {
    index: bestIndex,
    distance: bestDistance,
    secondBestDistance,
    ratio,
  };
}

// Lowe's ratio test in both directions followed by reciprocal matching.
export function matchDescriptors(
  descriptorsA,
  descriptorsB,
  ratioThreshold = 0.75,
) {
  if (!descriptorsA || !descriptorsB) {
    return [];
  }

  if (descriptorsA.cols !== descriptorsB.cols) {
    throw new Error("Descriptor lengths do not match.");
  }

  const forwardMatches = new Array(descriptorsA.rows);
  const reverseMatches = new Array(descriptorsB.rows);

  // A -> B
  for (let indexA = 0; indexA < descriptorsA.rows; indexA += 1) {
    forwardMatches[indexA] = findBestMatch(descriptorsA, indexA, descriptorsB);
  }

  // B -> A
  for (let indexB = 0; indexB < descriptorsB.rows; indexB += 1) {
    reverseMatches[indexB] = findBestMatch(descriptorsB, indexB, descriptorsA);
  }

  const matches = [];

  for (let indexA = 0; indexA < forwardMatches.length; indexA += 1) {
    const forward = forwardMatches[indexA];

    if (!forward || forward.ratio >= ratioThreshold) {
      continue;
    }

    const indexB = forward.index;
    const reverse = reverseMatches[indexB];

    if (!reverse || reverse.ratio >= ratioThreshold) {
      continue;
    }

    // Mutual nearest-neighbor check.
    if (reverse.index !== indexA) {
      continue;
    }

    matches.push({
      indexA,
      indexB,
      distance: forward.distance,
      secondBestDistance: forward.secondBestDistance,
      ratio: forward.ratio,
    });
  }

  matches.sort((matchA, matchB) => matchA.distance - matchB.distance);

  return matches;
}
